/**
 * The widget customiser, with a live preview.
 *
 * The preview is a rendering of the real widget's chrome rather than the real
 * widget: mounting the actual bundle here would need a site key, an allowed
 * origin for the dashboard's domain, and a live API — three things that are
 * wrong in exactly the situation someone is most likely to be customising
 * (a store that is not connected yet). What it shows instead is the same
 * palette, the same layout and the same strings, driven by the same config.
 */

import { useEffect, useState } from "react";

import { useSite, useUpdateSite } from "../../api/queries";
import type { WidgetConfig } from "../../api/types";
import {
  Button,
  Card,
  ErrorNote,
  Field,
  Input,
  Loading,
  Select,
  Textarea,
} from "../../ui/primitives";
import { useSiteContext } from "./SiteLayout";

const DEFAULT_ACCENT = "#2563eb";

/** Matches the widget's own validation, so the preview cannot lie. */
const HEX = /^#(?:[0-9a-f]{3}|[0-9a-f]{6})$/i;

export function WidgetPage() {
  const { orgId, siteId } = useSiteContext();
  const site = useSite(orgId, siteId);
  const update = useUpdateSite(orgId, siteId);

  const [config, setConfig] = useState<WidgetConfig | null>(null);

  // Seed the form once the site arrives, and again if it changes underneath
  // (another tab, or a save). Editing state is local so typing does not
  // round-trip.
  useEffect(() => {
    if (site.data) setConfig(site.data.widget_config ?? {});
  }, [site.data]);

  if (site.isLoading || !config) return <Loading />;
  if (site.error) return <ErrorNote error={site.error} />;

  function set<K extends keyof WidgetConfig>(key: K, value: WidgetConfig[K]) {
    setConfig((current) => ({ ...(current ?? {}), [key]: value }));
  }

  const accentValid = !config.accent || HEX.test(config.accent);

  return (
    <div className="grid gap-4 lg:grid-cols-[1fr_20rem]">
      <Card
        title="Appearance and behaviour"
        action={
          <Button
            variant="primary"
            size="sm"
            disabled={update.isPending || !accentValid}
            onClick={() => update.mutate({ widget_config: config })}
          >
            {update.isPending ? "Saving…" : "Save"}
          </Button>
        }
      >
        <div className="space-y-4">
          {update.error != null && <ErrorNote error={update.error} />}

          <Field
            label="Store name"
            id="store-name"
            hint="Shown in the widget header and used in the assistant's instructions."
          >
            <Input
              id="store-name"
              value={config.store_name ?? ""}
              placeholder={site.data?.name}
              onChange={(event) => set("store_name", event.target.value)}
            />
          </Field>

          <Field label="Greeting" id="greeting" hint="The first thing a visitor reads.">
            <Input
              id="greeting"
              value={config.greeting ?? ""}
              placeholder="Hi! Ask me about our products, delivery or returns."
              onChange={(event) => set("greeting", event.target.value)}
            />
          </Field>

          <div className="grid gap-4 sm:grid-cols-3">
            <Field
              label="Accent colour"
              id="accent"
              error={accentValid ? null : "Use a hex colour such as #2563eb."}
            >
              <div className="flex gap-2">
                <input
                  id="accent"
                  type="color"
                  className="h-9 w-12 rounded border border-line"
                  value={HEX.test(config.accent ?? "") ? config.accent : DEFAULT_ACCENT}
                  onChange={(event) => set("accent", event.target.value)}
                />
                <Input
                  aria-label="Accent colour hex value"
                  value={config.accent ?? ""}
                  placeholder={DEFAULT_ACCENT}
                  onChange={(event) => set("accent", event.target.value)}
                />
              </div>
            </Field>

            <Field label="Position" id="position">
              <Select
                id="position"
                value={config.position ?? "right"}
                onChange={(event) => set("position", event.target.value as "right" | "left")}
              >
                <option value="right">Bottom right</option>
                <option value="left">Bottom left</option>
              </Select>
            </Field>

            <Field label="Theme" id="theme">
              <Select
                id="theme"
                value={config.theme ?? "auto"}
                onChange={(event) =>
                  set("theme", event.target.value as "auto" | "light" | "dark")
                }
              >
                <option value="auto">Match visitor</option>
                <option value="light">Light</option>
                <option value="dark">Dark</option>
              </Select>
            </Field>
          </div>

          <Field
            label="About the store"
            id="about"
            hint="Context the assistant should always have. Two or three sentences."
          >
            <Textarea
              id="about"
              rows={3}
              value={config.store_description ?? ""}
              onChange={(event) => set("store_description", event.target.value)}
            />
          </Field>

          <Field
            label="Extra instructions"
            id="instructions"
            hint={
              "Tone and preferences. These cannot override the safety rules — " +
              "the assistant will still refuse to invent prices, stock or promises."
            }
          >
            <Textarea
              id="instructions"
              rows={3}
              placeholder="Always mention that shipping is free over €100."
              value={config.assistant_instructions ?? ""}
              onChange={(event) => set("assistant_instructions", event.target.value)}
            />
          </Field>

          <Field
            label="Privacy policy URL"
            id="privacy"
            hint="Linked from the consent notice shown before the first message."
          >
            <Input
              id="privacy"
              type="url"
              placeholder="https://shop.example.com/privacy"
              value={config.privacy_url ?? ""}
              onChange={(event) => set("privacy_url", event.target.value)}
            />
          </Field>
        </div>
      </Card>

      <Preview config={config} fallbackName={site.data?.name ?? "Your store"} />
    </div>
  );
}

function Preview({ config, fallbackName }: { config: WidgetConfig; fallbackName: string }) {
  const accent = HEX.test(config.accent ?? "") ? config.accent! : DEFAULT_ACCENT;
  const dark = config.theme === "dark";

  return (
    <Card title="Preview">
      <div
        className="rounded-xl border p-3"
        style={{
          background: dark ? "#16181d" : "#ffffff",
          borderColor: dark ? "#2c2f36" : "#e4e6eb",
          color: dark ? "#f2f3f5" : "#16181d",
        }}
      >
        <div
          className="mb-3 border-b pb-2 text-sm font-semibold"
          style={{ borderColor: dark ? "#2c2f36" : "#e4e6eb" }}
        >
          {config.store_name || fallbackName}
        </div>

        <div
          className="mb-2 inline-block max-w-[85%] rounded-xl px-3 py-2 text-sm"
          style={{ background: dark ? "#21242b" : "#f5f6f8" }}
        >
          {config.greeting || "Hi! Ask me about our products, delivery or returns."}
        </div>

        <div className="text-right">
          <span
            className="inline-block max-w-[85%] rounded-xl px-3 py-2 text-sm text-white"
            style={{ background: accent }}
          >
            Do you ship to Germany?
          </span>
        </div>

        <div
          className="mt-3 flex items-center gap-2 border-t pt-2"
          style={{ borderColor: dark ? "#2c2f36" : "#e4e6eb" }}
        >
          <span className="flex-1 text-sm opacity-50">Ask a question…</span>
          <span
            className="grid h-7 w-7 place-items-center rounded-full text-white"
            style={{ background: accent }}
            aria-hidden="true"
          >
            ↑
          </span>
        </div>
      </div>

      <div className={`mt-3 flex ${config.position === "left" ? "" : "justify-end"}`}>
        <span
          className="grid h-10 w-10 place-items-center rounded-full text-white shadow"
          style={{ background: accent }}
          aria-hidden="true"
        >
          ●
        </span>
      </div>

      <p className="mt-2 text-xs text-muted">
        Changes take effect on your store as soon as you save — visitors do not
        need to clear anything.
      </p>
    </Card>
  );
}
